import React, { useState, useEffect } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import BilingualInput from '../../components/admin/BilingualInput';
import BilingualTextArea from '../../components/admin/BilingualTextArea';
import ClientsEditorFields from '../../components/admin/ClientsEditorFields';
import ClientLogoUploadGrid from '../../components/admin/ClientLogoUploadGrid';
import { useNavigate } from 'react-router-dom';
import {
    Type,
    HardHat,
    Loader2,
    CheckCircle2,
    Save,
    Layout,
    ChevronDown,
    ChevronUp,
    RefreshCw,
    FileText,
    Briefcase,
    PhoneCall,
    Image as ImageIcon,
    AlertCircle,
    Users,
    Wrench,
    Map
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const AdminConstruction: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState<string | null>(null);
    const [content, setContent] = useState<any>({});
    const [pendingImages, setPendingImages] = useState<{ [key: string]: { file: File, preview: string } }>({});
    const [activeTab, setActiveTab] = useState<'text' | 'media'>('text');
    const [expandedSections, setExpandedSections] = useState<string[]>(['Hero Banner Settings', 'Enterprise Profile']);
    const [message, setMessage] = useState<{ text: string, type: 'success' | 'error' | '' }>({ text: '', type: '' });
    const [activeConstructionBranches, setActiveConstructionBranches] = useState<number[]>([]);
    const username = localStorage.getItem('adminUser') || 'Admin';

    const handleSessionExpired = () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        setMessage({ text: 'Session expired. Please login again.', type: 'error' });
        setTimeout(() => navigate('/admin/login'), 800);
    };

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            navigate('/admin/login');
        } else {
            fetchContent();
        }
    }, [navigate]);

    const fetchContent = async () => {
        setLoading(true);
        try {
            let data: any = {};

            try {
                const sectionResponse = await fetch('/api/content/Construction');
                if (!sectionResponse.ok) throw new Error('Section content fetch failed');
                const sectionData = await sectionResponse.json();
                data = { Construction: sectionData };
            } catch {
                const response = await fetch('/api/homepage/content');
                if (!response.ok) throw new Error('Homepage content fetch failed');
                data = await response.json();
            }

            if (!data.Construction || Object.keys(data.Construction).length === 0) {
                data.Construction = {
                    banner_title: "Hamdan Construction",
                    banner_subtitle: "Today engaged in large scale civil and private instruction activities across the sultanate of oman",
                    about_kicker: "Hamdan Group - Oman",
                    about_title: "CONSTRUCTION SECTOR",
                    about_desc: "The construction sector of Hamdan Group is one of the most prominent divisions...",
                    about_note_title: "Quick Note",
                    about_note_desc: "Large-scale civil and private construction capabilities across Oman.",
                    about_image_label: "Hamdan Construction",
                    proj_title: "Our Projects",
                    contact_form_title: "GET IN TOUCH",
                    contact_info_title: "CONTACT INFO",
                    contact_info_desc: "We are here to help you with your construction needs."
                };
            }
            setContent(data);
            if (data.Construction) {
                const ids = [];
                for (let i = 1; i <= 40; i++) {
                    if (data.Construction[`br_${i}_name`]) {
                        ids.push(i);
                    }
                }
                setActiveConstructionBranches(ids);
            }
        } catch (err) {
            console.error('Failed to fetch content');
        } finally {
            setLoading(false);
        }
    };

    const toggleSection = (name: string) => {
        setExpandedSections(prev =>
            prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]
        );
    };

    const updateField = (key: string, value: string) => {
        setContent({
            ...content,
            Construction: { ...(content.Construction || {}), [key]: value }
        });
    };

    const saveSectionText = async (sectionId: string) => {
        setSaving(sectionId);
        const sectionData = content.Construction || {};
        
        const fieldsToSave: { [key: string]: string } = {};
        if (sectionId === 'banner') {
            fieldsToSave['banner_title'] = sectionData.banner_title ?? '';
            fieldsToSave['banner_subtitle'] = sectionData.banner_subtitle ?? '';
        } else if (sectionId === 'about') {
            fieldsToSave['about_kicker'] = sectionData.about_kicker ?? '';
            fieldsToSave['about_title'] = sectionData.about_title ?? '';
            fieldsToSave['about_subtitle'] = sectionData.about_subtitle ?? '';
            fieldsToSave['about_desc'] = sectionData.about_desc ?? '';
            fieldsToSave['about_note_title'] = sectionData.about_note_title ?? '';
            fieldsToSave['about_note_desc'] = sectionData.about_note_desc ?? '';
            fieldsToSave['about_image_label'] = sectionData.about_image_label ?? '';
            [1, 2, 3].forEach(id => {
                fieldsToSave[`about_pt_${id}_title`] = sectionData[`about_pt_${id}_title`] ?? '';
                fieldsToSave[`about_pt_${id}_desc`] = sectionData[`about_pt_${id}_desc`] ?? '';
                fieldsToSave[`about_pt_${id}_icon`] = sectionData[`about_pt_${id}_icon`] ?? 'MapPin';
            });
        } else if (sectionId === 'services') {
            fieldsToSave['services_title'] = sectionData.services_title ?? '';
            [1, 2, 3].forEach(id => {
                fieldsToSave[`svc_${id}_title`] = sectionData[`svc_${id}_title`] ?? '';
                fieldsToSave[`svc_${id}_desc`] = sectionData[`svc_${id}_desc`] ?? '';
            });
        } else if (sectionId === 'projects') {
            fieldsToSave['proj_title'] = sectionData.proj_title ?? '';
            [1, 2, 3, 4, 5, 6].forEach(id => {
                fieldsToSave[`proj_${id}_title`] = sectionData[`proj_${id}_title`] ?? '';
                fieldsToSave[`proj_${id}_desc`] = sectionData[`proj_${id}_desc`] ?? '';
                fieldsToSave[`proj_${id}_cat`] = sectionData[`proj_${id}_cat`] ?? '';
                fieldsToSave[`proj_${id}_status`] = sectionData[`proj_${id}_status`] ?? '';
                fieldsToSave[`proj_${id}_icon`] = sectionData[`proj_${id}_icon`] ?? 'Building2';
            });
        } else if (sectionId === 'clients') {
            fieldsToSave['clients_title'] = sectionData.clients_title ?? '';
            fieldsToSave['clients_desc'] = sectionData.clients_desc ?? '';
            fieldsToSave['clients_list'] = sectionData.clients_list ?? '';
            fieldsToSave['clients_logo_urls'] = sectionData.clients_logo_urls ?? '';
            fieldsToSave['clients_footer'] = sectionData.clients_footer ?? '';
            fieldsToSave['clients_footer_desc'] = sectionData.clients_footer_desc ?? '';
        } else if (sectionId === 'contact') {
            fieldsToSave['contact_form_title'] = sectionData.contact_form_title ?? '';
            fieldsToSave['contact_info_title'] = sectionData.contact_info_title ?? '';
            fieldsToSave['contact_info_desc'] = sectionData.contact_info_desc ?? '';
            fieldsToSave['contact_support_email'] = sectionData.contact_support_email ?? '';
            fieldsToSave['contact_loc_name'] = sectionData.contact_loc_name ?? '';
            fieldsToSave['contact_loc_addr'] = sectionData.contact_loc_addr ?? '';
            fieldsToSave['contact_loc_phone'] = sectionData.contact_loc_phone ?? '';
            fieldsToSave['contact_loc_fax'] = sectionData.contact_loc_fax ?? '';
        } else if (sectionId === 'branches') {
            for (let i = 1; i <= 40; i++) {
                if (sectionData[`br_${i}_name`] === undefined) continue;
                ['name', 'addr', 'phone', 'fax', 'coord'].forEach(f => {
                    fieldsToSave[`br_${i}_${f}`] = sectionData[`br_${i}_${f}`] ?? '';
                });
            }
        }

        try {
            const promises = Object.entries(fieldsToSave).map(([key, value]) => {
                return fetch('/api/homepage/update-text', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                    },
                    body: JSON.stringify({ 
                        section: 'Construction', 
                        content_key: key, 
                        content_value: value,
                        content_value_ar: sectionData[`${key}_ar`] || null
                    })
                });
            });

            const responses = await Promise.all(promises);
            if (responses.some(response => response.status === 401 || response.status === 403)) {
                handleSessionExpired();
                return;
            }
            if (responses.some(response => !response.ok)) {
                throw new Error('Failed to update content');
            }

            setMessage({ text: 'Content updated successfully!', type: 'success' });
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);
        } catch (err) {
            setMessage({ text: 'Sync failed.', type: 'error' });
        } finally {
            setSaving(null);
        }
    };

    const handleImageSelect = (key: string, file: File) => {
        const preview = URL.createObjectURL(file);
        setPendingImages(prev => ({
            ...prev,
            [key]: { file, preview }
        }));
    };

    const saveAllPendingImages = async () => {
        if (Object.keys(pendingImages).length === 0) return;

        setSaving('all-images');
        try {
            const promises = Object.entries(pendingImages).map(async ([key, pending]) => {
                const formData = new FormData();
                formData.append('section', 'Construction');
                formData.append('content_key', key);
                formData.append('image', pending.file);

                const response = await fetch('/api/homepage/update-image', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                    },
                    body: formData
                });

                if (response.status === 401 || response.status === 403) {
                    throw new Error('SESSION_EXPIRED');
                }

                if (response.ok) {
                    const data = await response.json();
                    return { key, imageUrl: data.imageUrl };
                }

                throw new Error('Image upload failed');
            });

            const results = await Promise.all(promises);

            setContent((prev: any) => ({
                ...prev,
                Construction: {
                    ...(prev.Construction || {}),
                    ...results.reduce((acc: any, res) => {
                        if (res) acc[res.key] = res.imageUrl;
                        return acc;
                    }, {})
                }
            }));

            setPendingImages({});
            setMessage({ text: 'Media assets deployed!', type: 'success' });
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);
        } catch (err) {
            if (err instanceof Error && err.message === 'SESSION_EXPIRED') {
                handleSessionExpired();
                return;
            }
            setMessage({ text: 'Media upload failed.', type: 'error' });
        } finally {
            setSaving(null);
        }
    };

    const SectionHeader = ({ title, icon: Icon, sectionId }: any) => (
        <div className="flex items-center justify-between p-6 bg-white rounded-3xl border border-slate-100 shadow-sm mb-4">
            <div className="flex items-center space-x-4 cursor-pointer" onClick={() => toggleSection(title)}>
                <div className={`p-3 rounded-2xl transition-colors ${expandedSections.includes(title) ? 'bg-blue-600 text-white shadow-lg' : 'bg-blue-50 text-blue-600'}`}>
                    <Icon className="w-6 h-6" />
                </div>
                <div>
                    <h2 className="text-xl font-black text-slate-800 tracking-tight">{title}</h2>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Section Configuration</p>
                </div>
                {expandedSections.includes(title) ? <ChevronUp className="w-5 h-5 text-slate-300" /> : <ChevronDown className="w-5 h-5 text-slate-300" />}
            </div>
            {sectionId && (
                <button
                    onClick={(e) => { e.stopPropagation(); saveSectionText(sectionId); }}
                    disabled={!!saving}
                    className="flex items-center space-x-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-200 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-xl shadow-emerald-100 active:scale-95"
                >
                    {saving === sectionId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    <span>Update Section</span>
                </button>
            )}
        </div>
    );

    const Label = ({ children }: any) => (
        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 block">{children}</label>
    );

    const UploadBox = ({ keyName, label, currentImage }: any) => {
        const pending = pendingImages[keyName];
        const displayImage = pending ? pending.preview : currentImage;

        return (
            <div className="group bg-white p-6 rounded-3xl border border-slate-100 shadow-sm hover:border-blue-200 transition-all">
                <div className="flex justify-between items-center mb-6">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</span>
                    {currentImage && !pending && (
                        <div className="flex items-center gap-1.5 text-emerald-500 bg-emerald-50 px-2 py-1 rounded-lg">
                            <CheckCircle2 className="w-3 h-3" />
                            <span className="text-[8px] font-black uppercase">Live</span>
                        </div>
                    )}
                </div>

                <div className="relative aspect-video bg-slate-50 rounded-2xl overflow-hidden mb-5 border border-slate-100 flex items-center justify-center ring-offset-4 ring-blue-500/0 group-hover:ring-4 transition-all">
                    {displayImage ? (
                        <img src={displayImage} className="w-full h-full object-cover transition-transform group-hover:scale-105" alt="Preview" />
                    ) : (
                        <div className="flex flex-col items-center gap-2">
                            <ImageIcon className="w-8 h-8 text-slate-200" />
                            <span className="text-[10px] text-slate-300 font-bold uppercase">No Image</span>
                        </div>
                    )}
                    <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[1px]">
                        <label className="cursor-pointer bg-white text-blue-600 p-4 rounded-2xl font-bold shadow-2xl flex items-center space-x-2 transition-transform hover:scale-105">
                            <RefreshCw className="w-4 h-4" />
                            <input
                                type="file"
                                className="hidden"
                                accept="image/*"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleImageSelect(keyName, file);
                                }}
                            />
                        </label>
                    </div>
                </div>
                {pending && <p className="text-amber-500 text-[8px] font-black uppercase text-center animate-pulse">Pending Save</p>}
            </div>
        );
    };

    return (
        <div className="flex h-screen bg-slate-50 font-sans overflow-hidden">
            <AdminSidebar />

            {/* Standard Primary Area */}
            <div className="flex-1 flex flex-col h-full bg-slate-50/50 relative">
                <header className="h-20 bg-white border-b border-slate-200 flex items-center justify-between px-10 shadow-sm shrink-0 z-30">
                    <div className="flex items-center space-x-6">
                        <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2">
                            <HardHat className="w-6 h-6 text-blue-600" /> Construction Editor
                        </h2>

                        <div className="flex bg-slate-100 p-1.5 rounded-2xl">
                            <button
                                onClick={() => setActiveTab('text')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'text' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <Type className="w-4 h-4" />
                                <span>Text Content</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('media')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'media' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <ImageIcon className="w-4 h-4" />
                                <span>Media Assets</span>
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center space-x-6">
                        {saving && (
                            <div className="flex items-center space-x-2 text-blue-600 font-bold text-xs">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                <span className="animate-pulse">Syncing...</span>
                            </div>
                        )}
                        <div className="hidden sm:block text-right">
                            <div className="text-sm font-bold text-slate-900">{username}</div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Sector Manager</div>
                        </div>
                        <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center font-bold text-white border border-slate-700 shadow-inner">
                            {username[0]}
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto p-10 custom-scrollbar scroll-smooth">
                    <div className="max-w-5xl mx-auto">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-40">
                                <div className="w-16 h-16 border-4 border-slate-105 border-t-blue-600 rounded-full animate-spin mb-6" />
                                <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Loading...</p>
                            </div>
                        ) : (
                            <AnimatePresence mode="wait">
                                {activeTab === 'text' ? (
                                    <motion.div key="text" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6">
                                        
                                        <SectionHeader title="Hero Banner Settings" icon={Layout} sectionId="banner" />
                                        <AnimatePresence>
                                            {expandedSections.includes('Hero Banner Settings') && (
                                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                                    <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6 ml-4 mb-8">
                                                        <BilingualInput 
                                                            label="Hero Main Title"
                                                            englishValue={content.Construction?.banner_title}
                                                            arabicValue={content.Construction?.banner_title_ar}
                                                            onChangeEnglish={val => updateField('banner_title', val)}
                                                            onChangeArabic={val => updateField('banner_title_ar', val)}
                                                        />
                                                        <BilingualTextArea 
                                                            label="Hero Subtitle"
                                                            englishValue={content.Construction?.banner_subtitle}
                                                            arabicValue={content.Construction?.banner_subtitle_ar}
                                                            onChangeEnglish={val => updateField('banner_subtitle', val)}
                                                            onChangeArabic={val => updateField('banner_subtitle_ar', val)}
                                                            rows={2}
                                                        />
                                                        <div className="pt-6 border-t border-slate-100">
                                                             <Label>Key Performance Pillars</Label>
                                                             <div className="space-y-8">
                                                                {[1, 2, 3].map(i => (
                                                                    <div key={i} className="bg-slate-50 p-6 rounded-2xl border border-slate-100 space-y-4">
                                                                        <div className="flex justify-between items-center mb-2">
                                                                            <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs">{i}</span>
                                                                            <select className="text-[10px] font-bold bg-white border border-slate-200 rounded-lg p-1 outline-none" value={content.Construction?.[`about_pt_${i}_icon`] || 'MapPin'} onChange={e => updateField(`about_pt_${i}_icon`, e.target.value)}>
                                                                                <option value="Star">Star</option>
                                                                                <option value="MapPin">Place</option>
                                                                                <option value="Plane">Plane</option>
                                                                                <option value="Zap">Zap</option>
                                                                            </select>
                                                                        </div>
                                                                        <BilingualInput 
                                                                            label="Objective Name"
                                                                            englishValue={content.Construction?.[`about_pt_${i}_title`]}
                                                                            arabicValue={content.Construction?.[`about_pt_${i}_title_ar`]}
                                                                            onChangeEnglish={val => updateField(`about_pt_${i}_title`, val)}
                                                                            onChangeArabic={val => updateField(`about_pt_${i}_title_ar`, val)}
                                                                        />
                                                                        <BilingualTextArea 
                                                                            label="Detail summary..."
                                                                            englishValue={content.Construction?.[`about_pt_${i}_desc`]}
                                                                            arabicValue={content.Construction?.[`about_pt_${i}_desc_ar`]}
                                                                            onChangeEnglish={val => updateField(`about_pt_${i}_desc`, val)}
                                                                            onChangeArabic={val => updateField(`about_pt_${i}_desc_ar`, val)}
                                                                            rows={3}
                                                                        />
                                                                    </div>
                                                                ))}
                                                             </div>
                                                         </div>
                                                     </div>
                                                 </motion.div>
                                             )}
                                         </AnimatePresence>

                                         <SectionHeader title="Services Configuration" icon={Wrench} sectionId="services" />
                                         <AnimatePresence>
                                             {expandedSections.includes('Services Configuration') && (
                                                 <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                                     <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6 ml-4 mb-8">
                                                         <BilingualInput 
                                                             label="Section Title"
                                                             englishValue={content.Construction?.services_title}
                                                             arabicValue={content.Construction?.services_title_ar}
                                                             onChangeEnglish={val => updateField('services_title', val)}
                                                             onChangeArabic={val => updateField('services_title_ar', val)}
                                                         />
                                                         <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                                             {[1, 2, 3].map(i => (
                                                                 <div key={i} className="p-6 bg-slate-50 rounded-2xl border border-slate-100 space-y-4">
                                                                     <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Service {i}</span>
                                                                     <BilingualInput 
                                                                         label="Title"
                                                                         englishValue={content.Construction?.[`svc_${i}_title`]}
                                                                         arabicValue={content.Construction?.[`svc_${i}_title_ar`]}
                                                                         onChangeEnglish={val => updateField(`svc_${i}_title`, val)}
                                                                         onChangeArabic={val => updateField(`svc_${i}_title_ar`, val)}
                                                                     />
                                                                     <BilingualTextArea 
                                                                         label="Description"
                                                                         englishValue={content.Construction?.[`svc_${i}_desc`]}
                                                                         arabicValue={content.Construction?.[`svc_${i}_desc_ar`]}
                                                                         onChangeEnglish={val => updateField(`svc_${i}_desc`, val)}
                                                                         onChangeArabic={val => updateField(`svc_${i}_desc_ar`, val)}
                                                                         rows={3}
                                                                     />
                                                                 </div>
                                                             ))}
                                                         </div>
                                                     </div>
                                                 </motion.div>
                                             )}
                                         </AnimatePresence>

                                         <SectionHeader title="Portfolio Grid" icon={Briefcase} sectionId="projects" />
                                         <AnimatePresence>
                                             {expandedSections.includes('Portfolio Grid') && (
                                                 <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                                     <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6 ml-4 mb-8">
                                                         <BilingualInput 
                                                             label="Section Title"
                                                             englishValue={content.Construction?.proj_title}
                                                             arabicValue={content.Construction?.proj_title_ar}
                                                             onChangeEnglish={val => updateField('proj_title', val)}
                                                             onChangeArabic={val => updateField('proj_title_ar', val)}
                                                         />
                                                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                             {[1, 2, 3, 4, 5, 6].map(i => (
                                                                 <div key={i} className="p-6 rounded-2xl border border-slate-100 bg-slate-50/50 space-y-4">
                                                                     <div className="flex justify-between items-center text-[10px] font-bold text-slate-400">
                                                                         <span>DEVELOPMENT 0{i}</span>
                                                                         <select className="bg-white border rounded p-1" value={content.Construction?.[`proj_${i}_icon`] || 'Building2'} onChange={e => updateField(`proj_${i}_icon`, e.target.value)}>
                                                                             <option value="Building2">Commercial</option>
                                                                             <option value="Home">Residential</option>
                                                                             <option value="Factory">Industrial</option>
                                                                             <option value="Landmark">Infrastructure</option>
                                                                         </select>
                                                                     </div>
                                                                     <BilingualInput 
                                                                         label="Asset Name"
                                                                         englishValue={content.Construction?.[`proj_${i}_title`]}
                                                                         arabicValue={content.Construction?.[`proj_${i}_title_ar`]}
                                                                         onChangeEnglish={val => updateField(`proj_${i}_title`, val)}
                                                                         onChangeArabic={val => updateField(`proj_${i}_title_ar`, val)}
                                                                     />
                                                                     <BilingualTextArea 
                                                                         label="Technical scope..."
                                                                         englishValue={content.Construction?.[`proj_${i}_desc`]}
                                                                         arabicValue={content.Construction?.[`proj_${i}_desc_ar`]}
                                                                         onChangeEnglish={val => updateField(`proj_${i}_desc`, val)}
                                                                         onChangeArabic={val => updateField(`proj_${i}_desc_ar`, val)}
                                                                         rows={2}
                                                                     />
                                                                     <div className="grid grid-cols-1 gap-4">
                                                                         <BilingualInput 
                                                                             label="SEGMENT (e.g. CIVIL)"
                                                                             englishValue={content.Construction?.[`proj_${i}_cat`]}
                                                                             arabicValue={content.Construction?.[`proj_${i}_cat_ar`]}
                                                                             onChangeEnglish={val => updateField(`proj_${i}_cat`, val)}
                                                                             onChangeArabic={val => updateField(`proj_${i}_cat_ar`, val)}
                                                                         />
                                                                         <BilingualInput 
                                                                             label="STATUS (e.g. READY)"
                                                                             englishValue={content.Construction?.[`proj_${i}_status`]}
                                                                             arabicValue={content.Construction?.[`proj_${i}_status_ar`]}
                                                                             onChangeEnglish={val => updateField(`proj_${i}_status`, val)}
                                                                             onChangeArabic={val => updateField(`proj_${i}_status_ar`, val)}
                                                                         />
                                                                     </div>
                                                                 </div>
                                                             ))}
                                                         </div>
                                                     </div>
                                                 </motion.div>
                                             )}
                                         </AnimatePresence>

                                         <SectionHeader title="Enterprise Profile" icon={FileText} sectionId="about" />
                                         <AnimatePresence>
                                             {expandedSections.includes('Enterprise Profile') && (
                                                 <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                                     <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6 ml-4 mb-8">
                                                         <BilingualInput 
                                                             label="Left Kicker Text"
                                                             englishValue={content.Construction?.about_kicker}
                                                             arabicValue={content.Construction?.about_kicker_ar}
                                                             onChangeEnglish={val => updateField('about_kicker', val)}
                                                             onChangeArabic={val => updateField('about_kicker_ar', val)}
                                                         />
                                                         <BilingualInput 
                                                             label="Section Headline"
                                                             englishValue={content.Construction?.about_title}
                                                             arabicValue={content.Construction?.about_title_ar}
                                                             onChangeEnglish={val => updateField('about_title', val)}
                                                             onChangeArabic={val => updateField('about_title_ar', val)}
                                                         />
                                                         <BilingualTextArea 
                                                             label="Full Industry Description"
                                                             englishValue={content.Construction?.about_desc}
                                                             arabicValue={content.Construction?.about_desc_ar}
                                                             onChangeEnglish={val => updateField('about_desc', val)}
                                                             onChangeArabic={val => updateField('about_desc_ar', val)}
                                                             rows={6}
                                                         />
                                                     </div>
                                                 </motion.div>
                                             )}
                                         </AnimatePresence>

                                         <SectionHeader title="Client Portfolio" icon={Users} sectionId="clients" />
                                         <AnimatePresence>
                                             {expandedSections.includes('Client Portfolio') && (
                                                 <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                                     <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm ml-4 mb-8">
                                                         <ClientsEditorFields
                                                             data={content.Construction}
                                                             updateField={updateField}
                                                             defaultDescription="Trusted construction clients and project partners across Oman."
                                                         />
                                                     </div>
                                                 </motion.div>
                                             )}
                                         </AnimatePresence>

                                         <SectionHeader title="Service Desk" icon={PhoneCall} sectionId="contact" />
                                         <AnimatePresence>
                                             {expandedSections.includes('Service Desk') && (
                                                 <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                                     <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6 ml-4 mb-8">
                                                         <div className="space-y-6">
                                                             <BilingualInput 
                                                                 label="Form Title"
                                                                 englishValue={content.Construction?.contact_form_title}
                                                                 arabicValue={content.Construction?.contact_form_title_ar}
                                                                 onChangeEnglish={val => updateField('contact_form_title', val)}
                                                                 onChangeArabic={val => updateField('contact_form_title_ar', val)}
                                                             />
                                                             <BilingualInput 
                                                                 label="Info Title"
                                                                 englishValue={content.Construction?.contact_info_title}
                                                                 arabicValue={content.Construction?.contact_info_title_ar}
                                                                 onChangeEnglish={val => updateField('contact_info_title', val)}
                                                                 onChangeArabic={val => updateField('contact_info_title_ar', val)}
                                                             />
                                                             <BilingualTextArea 
                                                                 label="Info Description"
                                                                 englishValue={content.Construction?.contact_info_desc}
                                                                 arabicValue={content.Construction?.contact_info_desc_ar}
                                                                 onChangeEnglish={val => updateField('contact_info_desc', val)}
                                                                 onChangeArabic={val => updateField('contact_info_desc_ar', val)}
                                                                 rows={2}
                                                             />
                                                         </div>
                                                         <div className="space-y-6 pt-6 border-t border-slate-100">
                                                             <BilingualInput 
                                                                 label="Support Email"
                                                                 englishValue={content.Construction?.contact_support_email}
                                                                 arabicValue={content.Construction?.contact_support_email_ar}
                                                                 onChangeEnglish={val => updateField('contact_support_email', val)}
                                                                 onChangeArabic={val => updateField('contact_support_email_ar', val)}
                                                             />
                                                             <BilingualInput 
                                                                 label="Location Name"
                                                                 englishValue={content.Construction?.contact_loc_name}
                                                                 arabicValue={content.Construction?.contact_loc_name_ar}
                                                                 onChangeEnglish={val => updateField('contact_loc_name', val)}
                                                                 onChangeArabic={val => updateField('contact_loc_name_ar', val)}
                                                             />
                                                             <BilingualInput 
                                                                 label="Location Address"
                                                                 englishValue={content.Construction?.contact_loc_addr}
                                                                 arabicValue={content.Construction?.contact_loc_addr_ar}
                                                                 onChangeEnglish={val => updateField('contact_loc_addr', val)}
                                                                 onChangeArabic={val => updateField('contact_loc_addr_ar', val)}
                                                             />
                                                             <BilingualInput 
                                                                 label="Location Phone"
                                                                 englishValue={content.Construction?.contact_loc_phone}
                                                                 arabicValue={content.Construction?.contact_loc_phone_ar}
                                                                 onChangeEnglish={val => updateField('contact_loc_phone', val)}
                                                                 onChangeArabic={val => updateField('contact_loc_phone_ar', val)}
                                                             />
                                                             <BilingualInput 
                                                                 label="Location Fax"
                                                                 englishValue={content.Construction?.contact_loc_fax}
                                                                 arabicValue={content.Construction?.contact_loc_fax_ar}
                                                                 onChangeEnglish={val => updateField('contact_loc_fax', val)}
                                                                 onChangeArabic={val => updateField('contact_loc_fax_ar', val)}
                                                             />
                                                         </div>
                                                     </div>
                                                 </motion.div>
                                             )}
                                         </AnimatePresence>

                                         {/* ─── BRANCH NETWORK ─── */}
                                         <SectionHeader title="Branch Network" icon={Map} sectionId="branches" />
                                         <AnimatePresence>
                                             {expandedSections.includes('Branch Network') && (() => {
                                                 const C = content.Construction || {};
                                                 const getBranches = () => {
                                                     return activeConstructionBranches.map(i => ({
                                                         i,
                                                         name: C[`br_${i}_name`] || '',
                                                         addr: C[`br_${i}_addr`] || '',
                                                         phone: C[`br_${i}_phone`] || '',
                                                         fax: C[`br_${i}_fax`] || '',
                                                         coord: C[`br_${i}_coord`] || ''
                                                     }));
                                                 };
                                                 const branches = getBranches();
                                                 const nextI = activeConstructionBranches.length > 0 ? Math.max(...activeConstructionBranches) + 1 : 1;
                                                 const handleAdd = () => {
                                                     updateField(`br_${nextI}_name`, 'New Branch');
                                                     updateField(`br_${nextI}_addr`, '');
                                                     updateField(`br_${nextI}_phone`, '');
                                                     updateField(`br_${nextI}_fax`, '');
                                                     updateField(`br_${nextI}_coord`, '');
                                                     setActiveConstructionBranches(prev => [...prev, nextI]);
                                                 };
                                                 const handleDelete = (idx: number) => {
                                                     if (!window.confirm('Delete this branch?')) return;
                                                     ['name', 'addr', 'phone', 'fax', 'coord'].forEach(f => updateField(`br_${idx}_${f}`, ''));
                                                     setActiveConstructionBranches(prev => prev.filter(id => id !== idx));
                                                 };
                                                 return (
                                                     <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                                         <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm ml-4 mb-8 space-y-4">
                                                             <div className="flex items-center justify-between">
                                                                 <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100 flex-1 mr-4">
                                                                     <p className="text-xs font-bold text-blue-700">{branches.length} branch(es) — Edit inline, then click "Update Section" to save.</p>
                                                                 </div>
                                                                 <button onClick={handleAdd} className="flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm transition-all shadow-lg">
                                                                     <span className="text-lg leading-none">+</span> Add Branch
                                                                 </button>
                                                             </div>
                                                             {branches.length === 0 && (
                                                                 <div className="text-center py-12 text-slate-400">
                                                                     <p className="text-sm font-bold">No branches configured yet.</p>
                                                                     <p className="text-xs mt-1">Click "Add Branch" to get started.</p>
                                                                 </div>
                                                             )}
                                                             <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                                 {branches.map((branch) => (
                                                                     <div key={branch.i} className="p-6 rounded-2xl border border-slate-100 bg-slate-50/50 space-y-4">
                                                                         <div className="flex justify-between items-center">
                                                                             <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">BRANCH #{branch.i}</span>
                                                                             <button onClick={() => handleDelete(branch.i)} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-[10px] font-black transition-all">🗑 Delete</button>
                                                                         </div>
                                                                         <BilingualInput label="Branch Name" englishValue={C[`br_${branch.i}_name`] || ''} arabicValue={C[`br_${branch.i}_name_ar`]} onChangeEnglish={val => updateField(`br_${branch.i}_name`, val)} onChangeArabic={val => updateField(`br_${branch.i}_name_ar`, val)} />
                                                                         <BilingualTextArea label="Address" englishValue={C[`br_${branch.i}_addr`] || ''} arabicValue={C[`br_${branch.i}_addr_ar`]} onChangeEnglish={val => updateField(`br_${branch.i}_addr`, val)} onChangeArabic={val => updateField(`br_${branch.i}_addr_ar`, val)} rows={2} />
                                                                         <div className="grid grid-cols-2 gap-4">
                                                                             <BilingualInput label="Phone" englishValue={C[`br_${branch.i}_phone`] || ''} arabicValue={C[`br_${branch.i}_phone_ar`]} onChangeEnglish={val => updateField(`br_${branch.i}_phone`, val)} onChangeArabic={val => updateField(`br_${branch.i}_phone_ar`, val)} />
                                                                             <BilingualInput label="Fax" englishValue={C[`br_${branch.i}_fax`] || ''} arabicValue={C[`br_${branch.i}_fax_ar`]} onChangeEnglish={val => updateField(`br_${branch.i}_fax`, val)} onChangeArabic={val => updateField(`br_${branch.i}_fax_ar`, val)} />
                                                                         </div>
                                                                         <BilingualInput label="GPS Coordinates" englishValue={C[`br_${branch.i}_coord`] || ''} arabicValue={C[`br_${branch.i}_coord_ar`]} onChangeEnglish={val => updateField(`br_${branch.i}_coord`, val)} onChangeArabic={val => updateField(`br_${branch.i}_coord_ar`, val)} placeholder="e.g. 23.575480, 58.354941" />
                                                                     </div>
                                                                 ))}
                                                             </div>
                                                         </div>
                                                     </motion.div>
                                                 );
                                             })()}
                                         </AnimatePresence>

                                     </motion.div>
                                 ) : (
                                     <motion.div key="media" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }} className="space-y-12 pb-24">
                                         <div className="flex items-center justify-between border-b border-slate-200 pb-8">
                                             <div>
                                                 <h3 className="text-3xl font-black text-slate-800 tracking-tight">Graphics Vault</h3>
                                                 <p className="text-slate-500 text-sm font-medium">Infrastructure and project site visuals</p>
                                             </div>
                                             <button
                                                 onClick={saveAllPendingImages}
                                                 disabled={Object.keys(pendingImages).length === 0 || !!saving}
                                                 className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-2xl font-bold transition-all shadow-xl shadow-blue-200 disabled:bg-slate-300 active:scale-95 flex items-center gap-2"
                                             >
                                                 {saving === 'all-images' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                                 <span>Deploy Graphics</span>
                                             </button>
                                         </div>

                                         <div className="space-y-20">
                                             <div>
                                                 <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-8 border-b pb-4">Principal Carousel Banners</h4>
                                                 <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                                     {[1, 2, 3].map(i => (
                                                         <div key={i} className="space-y-4">
                                                             <UploadBox keyName={`banner_${i}`} label={`Slide Matrix 0${i}`} currentImage={content.Construction?.[`banner_${i}`]} />
                                                         </div>
                                                     ))}
                                                 </div>
                                             </div>

                                             <div className="grid lg:grid-cols-2 gap-12 pt-8">
                                                 <div>
                                                     <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-8">About Page Main Image</h4>
                                                     <UploadBox keyName="about_intro_image" label="Large Image Beside Introduction" currentImage={content.Construction?.about_intro_image} />
                                                 </div>
                                                 <div>
                                                     <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-8">Primary Case Image</h4>
                                                     <UploadBox keyName="about_image" label="Sector Featured Asset" currentImage={content.Construction?.about_image} />
                                                 </div>
                                             </div>

                                             <div className="pt-8">
                                                 <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mb-8 border-b pb-4">Service Images</h4>
                                                 <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                                     <UploadBox keyName="svc_1_image" label="Service 1 Image" currentImage={content.Construction?.svc_1_image} />
                                                     <UploadBox keyName="svc_2_image" label="Service 2 Image" currentImage={content.Construction?.svc_2_image} />
                                                     <UploadBox keyName="svc_3_image" label="Service 3 Image" currentImage={content.Construction?.svc_3_image} />
                                                 </div>
                                             </div>

                                             <div className="pt-8">
                                                 <ClientLogoUploadGrid
                                                     data={content.Construction}
                                                     pendingImages={pendingImages}
                                                     onSelect={handleImageSelect}
                                                 />
                                             </div>
                                         </div>
                                     </motion.div>
                                 )}
                             </AnimatePresence>
                         )}
                         <div className="h-20" />
                     </div>
                 </main>
             </div>

             <AnimatePresence>
                 {message.text && (
                     <motion.div
                         initial={{ opacity: 0, y: 50 }}
                         animate={{ opacity: 1, y: 0 }}
                         exit={{ opacity: 0, y: 50 }}
                         className={`fixed bottom-10 right-10 z-[100] px-8 py-4 rounded-2xl shadow-2xl flex items-center gap-4 ${message.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}
                     >
                         {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                         <span className="font-bold text-sm tracking-tight">{message.text}</span>
                     </motion.div>
                 )}
             </AnimatePresence>
         </div>
     );
 };

 export default AdminConstruction;
